import { useEffect, useState, useMemo } from "react";
import moment from "moment";
import submit from "submit-form";
import getAvatarUrl from "../../../utils/get-avatar-url";
import SettingsNav from "../../../parts/settings-nav";
import SvgIcon from "../../../parts/SvgIcon";
import Dropdown from "../../../parts/dropdown";
import { SettingsBackHandler } from "../settings/components/SettingsBackHandler";

const Notifications = ({}, { locals }) => {
  const { coachNotifications = {}, query, activeUser } = locals;
  const { notifications = [], hasNextPage = false } = coachNotifications;
  const { page = 0, sort, type, status } = query;

  const [notificationList, setNotificationList] = useState([]);
  const [selectedList, setSelectedList] = useState([]);

  useEffect(() => {
    if (!page || page == 0) {
      setNotificationList(notifications);
    } else {
      setNotificationList((prevState) => {
        return [...prevState, ...notifications];
      });
    }
  }, [notifications]);

  const unread = useMemo(() => {
    if (status === "read") return 0;
    return notificationList.filter((notification) => !notification.read).length;
  }, [notificationList]);

  const loadMore = (e) => {
    e.preventDefault();
    submit("/actions/refresh", {
      method: "GET",
      body: {
        page: page + 1,
      },
    });
  };

  const handleSelectNotification = (e, id) => {
    e.preventDefault();
    setSelectedList((list) => {
      if (list.indexOf(id) > -1) {
        return list.filter((item) => item !== id);
      } else {
        return [...list, id];
      }
    });
  };

  const handleDeselectAll = (e) => {
    if (e) e.preventDefault();
    setSelectedList([]);
  };

  const handleSelectAll = (e) => {
    e.preventDefault();
    const selected = notificationList
      .filter(({ read }) => !read)
      .map(({ _id }) => _id);
    setSelectedList(selected);
  };

  const handleMarkRead = (e) => {
    e.preventDefault();
    submit(`/actions/notification/coach-notifications/read`, {
      method: "POST",
      body: {
        notifications: selectedList.length
          ? selectedList
          : notificationList.filter(({ read }) => !read).map(({ _id }) => _id),
      },
    });
    handleDeselectAll();
  };

  const allSelected = unread === selectedList.length;

  const handleSort = () => {
    submit("/actions/refresh", {
      method: "GET",
      body: {
        page: 0,
        sort: sort === "oldest" ? "newest" : "oldest",
      },
    });
  };
  const statusOptions = [
    { text: "All", value: "all" },
    { text: "Read", value: "read" },
    { text: "Unread", value: "unread" },
  ];
  const typeOptions = [
    { text: "All", value: "all" },
    { text: "Comment/Video", value: "Push" },
    { text: "Check-In", value: "CheckIn" },
  ];
  const checkNameOnText = (
    notificationText,
    athleteName,
    isAthlete = false
  ) => {
    if (notificationText.startsWith(athleteName) || isAthlete)
      return notificationText;
    const notificationWords = notificationText.split(" ");

    const removed = notificationWords.splice(0, 2, athleteName);
    return notificationWords.join(" ");
  };
  return (
    <main className="settings">
      <SettingsNav classNames="mobile-hidden" />
      <section className="notifications">
        <header>
          <h3 className="force-mobile-hidden">Notifications</h3>
          <SettingsBackHandler title={"Notifications"} />
          <hgroup>
            <div className="table-controls">
              <div className="input-group">
                <label htmlFor="status">Status</label>
                <Dropdown
                  name="status"
                  options={statusOptions}
                  onSelectFn={(newVal) => {
                    submit("/actions/refresh", {
                      method: "GET",
                      body: {
                        page: 0,
                        status: newVal,
                      },
                    });
                  }}
                  summaryText={
                    statusOptions.find((op) => op.value === status)?.text ||
                    "All"
                  }
                />
              </div>

              <div className="input-group">
                <label htmlFor="type">View</label>
                <Dropdown
                  name="type"
                  options={typeOptions}
                  onSelectFn={(newVal) => {
                    submit("/actions/refresh", {
                      method: "GET",
                      body: {
                        page: 0,
                        type: newVal,
                      },
                    });
                  }}
                  summaryText={
                    typeOptions.find((op) => op.value === type)?.text || "All"
                  }
                />
              </div>
            </div>
            {unread > 0 && (
              <button data-button-type="primary" onClick={handleMarkRead}>
                Mark {selectedList.length ? `${selectedList.length}` : "all"} as
                read
              </button>
            )}
          </hgroup>
        </header>

        {notificationList.length > 0 ? (
          <section
            className={`table-container${
              hasNextPage ? " with-pagination" : ""
            }`}
            role="table-wrapper"
            data-borders-inside
          >
            <table role="report-table">
              <thead>
                <tr>
                  {unread > 0 && (
                    <th className="select-cell">
                      <SvgIcon
                        src={
                          allSelected
                            ? "/img/new-icons/checked-square.svg"
                            : "/img/new-icons/unchecked-square.svg"
                        }
                        classNames="notification-select-all-box"
                        onClick={
                          allSelected ? handleDeselectAll : handleSelectAll
                        }
                        noFilterOnLight={allSelected}
                      />
                    </th>
                  )}
                  <th>
                    Date{" "}
                    <SvgIcon
                      onClickFn={handleSort}
                      src="/img/svg/up_down_arrows.svg"
                    />
                  </th>
                  <th>User</th>
                  <th>Description</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {notificationList.map(
                  ({ _id, date, text, read, button, requester }) => {
                    // no requester means athlete is no longer with this coach, don't show this notification
                    if (!requester) return null;
                    const isSelected = selectedList.includes(_id);

                    return (
                      <tr key={_id}>
                        {unread > 0 && (
                          <td className="select-cell">
                            {!read ? (
                              <SvgIcon
                                src={
                                  isSelected
                                    ? "/img/new-icons/checked-square.svg"
                                    : "/img/new-icons/unchecked-square.svg"
                                }
                                onClick={(e) =>
                                  handleSelectNotification(e, _id)
                                }
                                noFilterOnLight={isSelected}
                              />
                            ) : (
                              <SvgIcon src="/img/new-icons/read-notification.svg" />
                            )}
                          </td>
                        )}
                        <td>{moment(date).format("MMM D, YYYY")}</td>
                        <td className="user-cell">
                          <img
                            height={32}
                            width={32}
                            loading="lazy"
                            src={getAvatarUrl(requester)}
                          />

                          <span>{requester.name || "-"}</span>
                        </td>
                        <td>
                          {checkNameOnText(
                            text,
                            requester.name,
                            activeUser?.coach
                          )}
                        </td>
                        <td>
                          {button ? (
                            <a
                              role="button"
                              data-button-type="outline"
                              href={
                                button.link
                                  .replace("http://app.mystrengthbook.com", "")
                                  .replace(
                                    "https://app.mystrengthbook.com",
                                    ""
                                  ) + "&navFrom=notifications"
                              }
                              target={button.newTab ? "_blank" : "_self"}
                            >
                              <span>View</span>
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    );
                  }
                )}
              </tbody>
            </table>
          </section>
        ) : (
          <article>
            <h3>No Notifications</h3>
            <p className="p2">
              This page will display an overview of all your notifications.
            </p>
          </article>
        )}

        {hasNextPage && (
          <button data-button-type="secondary" onClick={loadMore}>
            Load More
          </button>
        )}
      </section>
    </main>
  );
};

const getCheckColors = (selected) => {
  const circleFill = selected ? "var(--green)" : "var(--white-20)";
  const pathFill = selected ? "var(--white-87)" : "transparent";

  return { circleFill, pathFill };
};

export default Notifications;
